import React, { createContext, useState, useContext, useCallback } from 'react';
import { NotificationChannelPref, NotificationPreferences } from '../types/notification.types';
import { safeGetJson, safeSetJson } from '../utils/safeStorage';

const STORAGE_KEY = 'ab3.notificationPreferences.v1';
const CURRENT_SCHEMA_VERSION = 1;

interface StoredPrefs extends NotificationPreferences {
  schema_version: number;
}

const DEFAULT_PREFS: NotificationPreferences = {
  toast: 'all',
  sound: 'all',
  os: 'all',
  dnd: { enabled: false },
};

function isValidChannelPref(val: unknown): val is NotificationChannelPref {
  return val === 'off' || val === 'warn_err' || val === 'all';
}

function isValidStoredPrefs(val: unknown): val is StoredPrefs {
  if (!val || typeof val !== 'object') return false;
  const v = val as Record<string, unknown>;
  if (v['schema_version'] !== CURRENT_SCHEMA_VERSION) return false;
  if (!isValidChannelPref(v['toast'])) return false;
  if (!isValidChannelPref(v['sound'])) return false;
  if (!isValidChannelPref(v['os'])) return false;
  if (!v['dnd'] || typeof v['dnd'] !== 'object') return false;
  const dnd = v['dnd'] as Record<string, unknown>;
  if (typeof dnd['enabled'] !== 'boolean') return false;
  if (dnd['until'] !== undefined && typeof dnd['until'] !== 'string') return false;
  return true;
}

function loadPrefs(): NotificationPreferences {
  const stored = safeGetJson<unknown>(STORAGE_KEY, null);
  if (!isValidStoredPrefs(stored)) return { ...DEFAULT_PREFS };
  return {
    toast: stored.toast,
    sound: stored.sound,
    os: stored.os,
    dnd: { enabled: stored.dnd.enabled, ...(stored.dnd.until ? { until: stored.dnd.until } : {}) },
  };
}

function persistPrefs(prefs: NotificationPreferences): void {
  const toStore: StoredPrefs = { ...prefs, schema_version: CURRENT_SCHEMA_VERSION };
  safeSetJson(STORAGE_KEY, toStore);
}

export interface NotificationPreferencesContextType {
  prefs: NotificationPreferences;
  setToastPref: (p: NotificationChannelPref) => void;
  setSoundPref: (p: NotificationChannelPref) => void;
  setOsPref: (p: NotificationChannelPref) => void;
  toggleDnd: (enabled: boolean, until?: string) => void;
  isDND: () => boolean;
}

const NotificationPreferencesContext = createContext<NotificationPreferencesContextType | undefined>(
  undefined
);

export const useNotificationPreferences = (): NotificationPreferencesContextType => {
  const ctx = useContext(NotificationPreferencesContext);
  if (!ctx) {
    throw new Error('useNotificationPreferences must be used within a NotificationPreferencesProvider');
  }
  return ctx;
};

export const NotificationPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [prefs, setPrefs] = useState<NotificationPreferences>(() => loadPrefs());

  const setToastPref = useCallback((p: NotificationChannelPref) => {
    setPrefs((prev) => {
      const next = { ...prev, toast: p };
      persistPrefs(next);
      return next;
    });
  }, []);

  const setSoundPref = useCallback((p: NotificationChannelPref) => {
    setPrefs((prev) => {
      const next = { ...prev, sound: p };
      persistPrefs(next);
      return next;
    });
  }, []);

  const setOsPref = useCallback((p: NotificationChannelPref) => {
    setPrefs((prev) => {
      const next = { ...prev, os: p };
      persistPrefs(next);
      return next;
    });
  }, []);

  const toggleDnd = useCallback((enabled: boolean, until?: string) => {
    setPrefs((prev) => {
      const next: NotificationPreferences = {
        ...prev,
        dnd: { enabled, ...(until ? { until } : {}) },
      };
      persistPrefs(next);
      return next;
    });
  }, []);

  const isDND = useCallback((): boolean => {
    const { enabled, until } = prefs.dnd;
    if (!enabled) return false;
    if (!until) return true;
    return new Date(until) > new Date();
  }, [prefs.dnd]);

  const value: NotificationPreferencesContextType = {
    prefs,
    setToastPref,
    setSoundPref,
    setOsPref,
    toggleDnd,
    isDND,
  };

  return (
    <NotificationPreferencesContext.Provider value={value}>
      {children}
    </NotificationPreferencesContext.Provider>
  );
};
